import dayjs from 'dayjs';
import { createSearchParams } from 'react-router-dom';
import { TableFilter } from '../metadata/propertyConfig';
import { DateRangeValue } from '../date-range-filter/DateRangeFilter';
import { LastOrderDateOptions } from '@/config/options.config';
import {
  restoreOrderFiltersFromQS,
  restorePropertyFiltersFromQS,
  createOrderParams,
  parseQueryString,
} from './advancedFilterUtils';

export interface FilterChip {
  /** Stable React key and removal target identifier. */
  id: string;
  /** Title-Case field name, e.g. "Order Date". */
  label: string;
  /** Human-readable current value. */
  value: string;
  onRemove: () => void;
}

export interface BuildFilterChipsArgs {
  isProperty: boolean;
  filter: TableFilter;
  orderStatusValues: { value: string; label: string }[];
  orderDateRange: DateRangeValue | null;
  selectedEntityFilters: { value: string; label: string }[];
  /** Orders preset active — status is rendered as a separate locked chip. */
  isLocked: boolean;
  /** The serialized advanced-filter query string (from MoreFiltersDrawer). */
  moreFilters: string;
  removeOrderStatus: (value: string) => void;
  removeOrderDate: () => void;
  removeEntity: (value: string) => void;
  removePropertyStatus: () => void;
  /** Re-apply the remaining advanced params after a per-field removal. */
  onRemoveAdvanced: (nextMoreFilters: string) => void;
  /** Maps a service shoppingCartSlug to its display name (orders only). */
  serviceNameBySlug?: Record<string, string>;
  /** Active "Properties Displayed" selection (properties only). */
  activeStatus?: { value: string; label: string } | null;
  /** Reset "Properties Displayed" to its Active default (properties only). */
  removeActiveStatus?: () => void;
}

/**
 * Chip value for a date range: MM/DD/YYYY for a custom from–to range, or the
 * friendly preset label (e.g. "Last 7 Days") for a named preset.
 */
const dateRangeChipValue = (range: DateRangeValue): string => {
  if (range.preset !== 'custom') return range.displayLabel;
  const fmt = (iso: string | null) => (iso ? dayjs(iso).format('MM/DD/YYYY') : '');
  return `${fmt(range.from)} – ${fmt(range.to)}`;
};

/** Re-serialize the property advanced params, dropping one query key. */
const serializePropertyAdvancedWithout = (
  moreFilters: string,
  omitKey: string
): string => {
  const values = parseQueryString(moreFilters);
  delete values[omitKey];
  return createSearchParams(values).toString();
};

/** Resolve a stored last-order-date value to its option label. */
const lastOrderDateLabel = (value: string): string =>
  LastOrderDateOptions.find((o) => o.value === value)?.label ?? value;

type AdvancedField =
  | 'unableToComplete'
  | 'accessType'
  | 'appointment'
  | 'completed'
  | 'services'
  | 'orderedBy';

/**
 * Re-serialize the order advanced params, omitting one logical field.
 * Uses the same createOrderParams → createSearchParams path the drawer uses,
 * so the output is compatible with onApplyFilters.
 */
const serializeOrderAdvancedWithout = (
  moreFilters: string,
  omit: AdvancedField
): string => {
  const r = restoreOrderFiltersFromQS(moreFilters);
  const params = createOrderParams({
    accessType: omit === 'accessType' ? undefined : r.simple.accessType,
    unableToComplete:
      omit === 'unableToComplete' ? undefined : r.unableToComplete ?? undefined,
    appointmentDateRange: omit === 'appointment' ? null : r.appointmentDateRange,
    orderCompletedDateRange: omit === 'completed' ? null : r.orderCompletedDateRange,
    services:
      omit === 'services'
        ? []
        : r.serviceSlugs.map((s) => ({ value: s, label: s })),
    orderedBy: omit === 'orderedBy' ? [] : r.orderedByValues,
  });
  // Band-aid: createOrderParams returns string | boolean | string[] values, but
  // createSearchParams' types want Record<string,string>. We cast to match the
  // existing MoreFiltersDrawer pattern; runtime output is correct (booleans/arrays
  // coerce as the drawer relies on). Proper fix (follow-up): change createOrderParams'
  // return type to Record<string,string|string[]> (stringify the unableToComplete
  // boolean) and drop the cast here and in MoreFiltersDrawer.
  return createSearchParams(params as Record<string, string>).toString();
};

/**
 * Derive the removable filter-chip model from the current filter bundle.
 * Pure: every chip's onRemove delegates to a setter supplied by the caller.
 * The search/address input is intentionally never chipped, and a
 * preset-locked Order Status is excluded (FilterTray renders it separately).
 *
 * For ORDERS, advanced filters (moreFilters) are expanded to one chip per
 * active field. For PROPERTIES, a single "More Filters (N)" count chip is
 * shown (no property restore helper exists).
 */
export const buildFilterChips = (args: BuildFilterChipsArgs): FilterChip[] => {
  const chips: FilterChip[] = [];

  if (args.isProperty) {
    if (args.filter.status) {
      chips.push({
        id: 'unitStatus',
        label: 'Unit Status',
        value: args.filter.status,
        onRemove: args.removePropertyStatus,
      });
    }
  } else {
    if (!args.isLocked) {
      args.orderStatusValues.forEach((s) => {
        chips.push({
          id: `status:${s.value}`,
          label: 'Order Status',
          value: s.label,
          onRemove: () => args.removeOrderStatus(s.value),
        });
      });
    }

    if (args.orderDateRange) {
      chips.push({
        id: 'orderDate',
        label: 'Order Date',
        value: dateRangeChipValue(args.orderDateRange),
        onRemove: args.removeOrderDate,
      });
    }

    args.selectedEntityFilters.forEach((e) => {
      chips.push({
        id: `entity:${e.value}`,
        label: 'Entity',
        value: e.label,
        onRemove: () => args.removeEntity(e.value),
      });
    });
  }

  // Advanced filter chips: one per active field, for both lists.
  if (args.isProperty) {
    const pr = restorePropertyFiltersFromQS(args.moreFilters);
    const omitProperty = (key: string) =>
      args.onRemoveAdvanced(
        serializePropertyAdvancedWithout(args.moreFilters, key)
      );

    if (pr.hasPhotos !== null) {
      chips.push({
        id: 'mf:hasPhotos',
        label: 'Photos',
        value: pr.hasPhotos === 'true' ? 'Complete' : 'None',
        onRemove: () => omitProperty('hasPhotos'),
      });
    }
    if (pr.has3DTour !== null) {
      chips.push({
        id: 'mf:has3DTour',
        label: '3D Tour',
        value: pr.has3DTour === 'true' ? 'Complete' : 'None',
        onRemove: () => omitProperty('has3DTour'),
      });
    }
    if (pr.lastOrderDateRange !== null) {
      chips.push({
        id: 'mf:lastOrderDate',
        label: 'Last Order Date',
        value: lastOrderDateLabel(pr.lastOrderDateRange),
        onRemove: () => omitProperty('lastOrderDateRange'),
      });
    }
    // "Properties Displayed" lives outside moreFilters; Active Units is the
    // default (no chip), Inactive Units → "Inactive", null → "All".
    if (
      args.activeStatus !== undefined &&
      args.activeStatus?.value !== 'Active Units'
    ) {
      chips.push({
        id: 'activeStatus',
        label: 'Properties Displayed',
        value: args.activeStatus?.value === 'Inactive Units' ? 'Inactive' : 'All',
        onRemove: args.removeActiveStatus ?? (() => {}),
      });
    }
  } else {
    const r = restoreOrderFiltersFromQS(args.moreFilters);

    if (r.unableToComplete !== null) {
      chips.push({
        id: 'mf:unableToComplete',
        label: 'Unable to Complete',
        value: r.unableToComplete ?? '',
        onRemove: () =>
          args.onRemoveAdvanced(
            serializeOrderAdvancedWithout(args.moreFilters, 'unableToComplete')
          ),
      });
    }

    if (r.simple.accessType) {
      chips.push({
        id: 'mf:accessType',
        label: 'Access Type',
        value: r.simple.accessType,
        onRemove: () =>
          args.onRemoveAdvanced(
            serializeOrderAdvancedWithout(args.moreFilters, 'accessType')
          ),
      });
    }

    if (r.appointmentDateRange) {
      chips.push({
        id: 'mf:appointment',
        label: 'Appointment Date',
        value: dateRangeChipValue(r.appointmentDateRange),
        onRemove: () =>
          args.onRemoveAdvanced(
            serializeOrderAdvancedWithout(args.moreFilters, 'appointment')
          ),
      });
    }

    if (r.orderCompletedDateRange) {
      chips.push({
        id: 'mf:completed',
        label: 'Order Completed Date',
        value: dateRangeChipValue(r.orderCompletedDateRange),
        onRemove: () =>
          args.onRemoveAdvanced(
            serializeOrderAdvancedWithout(args.moreFilters, 'completed')
          ),
      });
    }

    if (r.serviceSlugs.length > 0) {
      // Resolve slugs to display names (fall back to the slug until the filter
      // metadata loads); single → the name, multiple → "first +N".
      const names = r.serviceSlugs.map((s) => args.serviceNameBySlug?.[s] ?? s);
      const servicesValue =
        names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`;
      chips.push({
        id: 'mf:services',
        label: 'Services Ordered',
        value: servicesValue,
        onRemove: () =>
          args.onRemoveAdvanced(
            serializeOrderAdvancedWithout(args.moreFilters, 'services')
          ),
      });
    }

    if (r.orderedByValues.length > 0) {
      // Display value: count when multiple, single value when only one.
      const orderedByValue =
        r.orderedByValues.length === 1
          ? r.orderedByValues[0].value
          : String(r.orderedByValues.length);
      chips.push({
        id: 'mf:orderedBy',
        label: 'Ordered By',
        value: orderedByValue,
        onRemove: () =>
          args.onRemoveAdvanced(
            serializeOrderAdvancedWithout(args.moreFilters, 'orderedBy')
          ),
      });
    }
  }

  return chips;
};
